import {
  MAX_SUGGESTIONS,
  SUGGESTED_TAGS,
} from "@/src/constants/tags.constants";
import { CloseCircle, Tag } from "iconsax-react-nativejs";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  NativeSyntheticEvent,
  Pressable,
  Text,
  TextInput,
  TextInputKeyPressEventData,
  TouchableOpacity,
  View,
} from "react-native";
import { useTheme } from "@/src/styles/theme-provider";
import TagPill from "./tag-pill";

type PostBodyInputProps = Readonly<{
  value: string;
  hashtags: string[];
  onChange: (text: string) => void;
  onTagsChange: (tags: string[]) => void;
}>;

const getActiveDraft = (text: string): string | null => {
  const match = text.match(/(?:^|\s)#(\w+)$/);
  return match ? match[1].toLowerCase() : null;
};

const stripDraft = (text: string): string =>
  text.replace(/(\s?)#\w+$/, (_, space) => space ?? "");

const getFilteredSuggestions = (
  draft: string | null,
  activeTags: string[],
): string[] => {
  if (!draft || draft.length === 0) return [];
  return SUGGESTED_TAGS.filter(
    (tag) =>
      tag.toLowerCase().startsWith(draft) &&
      !activeTags.includes(tag.toLowerCase()),
  ).slice(0, MAX_SUGGESTIONS);
};

const PostBodyInput = ({
  value,
  hashtags,
  onChange,
  onTagsChange,
}: PostBodyInputProps) => {
  const { themeVars } = useTheme();
  const [tags, setTags] = useState<string[]>([]);
  const [draft, setDraft] = useState<string | null>(null);
  const [selection, setSelectionState] = useState<
    { start: number; end: number } | undefined
  >(undefined);
  const inputRef = useRef<TextInput>(null);

  useEffect(()=>{
    setTags(hashtags)
  },[hashtags]);

  const handleChangeText = (text: string) => {
    setSelectionState(undefined);
    onChange(text);
    setDraft(getActiveDraft(text));
  };

  const confirmTag = (tag: string) => {
    const normalizedTag = tag.toLowerCase();
    const updatedBody = stripDraft(value);
    const updatedTags = tags.includes(normalizedTag)
      ? tags
      : [...tags, normalizedTag];

    onChange(updatedBody);
    setTags(updatedTags);
    onTagsChange(updatedTags);
    setDraft(null);

    const cursorPosition = updatedBody.length;
    setSelectionState({ start: cursorPosition, end: cursorPosition });
    inputRef.current?.focus();
  };

  const removeTag = (tag: string) => {
    const updatedTags = tags.filter((t) => t !== tag);
    setTags(updatedTags);
    onTagsChange(updatedTags);
    inputRef.current?.focus();
  };

  const handleKeyPress = (
    e: NativeSyntheticEvent<TextInputKeyPressEventData>,
  ) => {
    const key = e.nativeEvent?.key;
    if ((key === " " || key === "Enter") && draft && draft.length > 0) {
      if (key === "Enter") e.preventDefault?.();
      confirmTag(draft);
    }
  };

  const handleSelectionChange = () => {
    if (selection !== undefined) setSelectionState(undefined);
  };

  const suggestions = getFilteredSuggestions(draft, tags);
  const hasSuggestions = suggestions.length > 0;
  const hasTags = tags.length > 0;
  const { t } = useTranslation();

  return (
    <View className="gap-3 w-full px-3">
      <TextInput
        ref={inputRef}
        value={value}
        onChangeText={handleChangeText}
        onKeyPress={handleKeyPress}
        selection={selection}
        onSelectionChange={handleSelectionChange}
        multiline
        placeholder="What's on your mind?"
        placeholderTextColor={themeVars["--color-slate-default"] as string}
        textAlignVertical="top"
        underlineColorAndroid="transparent"
        selectionColor={themeVars["--color-primary-default"] as string}
        className="min-h-[100px] w-full text-sm text-gray-dark focus:outline-none"
      />

      {hasSuggestions && (
        <View className="self-start bg-chalk rounded-xl border border-ash-3 overflow-hidden">
          <Text
            className="text-xs font-medium px-3 pt-2 pb-1"
            style={{ color: themeVars["--color-slate-default"] as string }}
          >
            {t("LABEL_TAG_NAME")}
          </Text>
          {suggestions.map((item, index) => {
            const isLast = index === suggestions.length - 1;
            return (
              <TouchableOpacity
                key={item}
                onPress={() => confirmTag(item)}
                className={`flex-row items-center gap-2 px-3 py-2 hover:bg-ash-3 ${isLast ? "pb-3" : "border-b border-ash-3"}`}
              >
                <Tag size={20} color={themeVars["--color-slate-default"] as string} variant="Bold" />
                <Text
                  className="text-sm font-semibold"
                  style={{ color: themeVars["--color-slate-default"] as string }}
                >
                  #{item}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>
      )}

      {hasTags && (
        <View className="flex-row flex-wrap gap-2 w-full">
          {tags.map((tag) => (
            <TagPill key={tag} tag={tag} onRemove={removeTag} isEditing={true}/>
          ))}
        </View>
      )}
    </View>
  );
};

export default PostBodyInput;
